<template>
    <section class="conversation-info" aria-label="Conversation statistics">
        <p v-if="directMessage && dateRange" class="conversation-info__dates">
            Direct messages from <deq-date :date="dateRange.start"/> up to, but not including, <deq-date :date="dateRange.end"/>.
            Includes irrelevant messages for context.
        </p>
        <p v-if="loading" role="status"><inline-loading-message>Loading conversation statistics…</inline-loading-message></p>
        <p v-else-if="error" role="alert">
            Unable to load conversation statistics. <button class="btn" @click="load">Try again</button>
        </p>
        <template v-else-if="stats">
            <dl>
                <div><dt>{{ directMessage ? 'Messages' : 'Mentions' }}</dt><dd><deq-number :number="stats.all.mentionCount"/></dd></div>
                <div><dt>Unique authors</dt><dd><deq-number :number="stats.all.authorIdCount"/></dd></div>
                <div v-if="!directMessage"><dt>Reshares</dt><dd><deq-number :number="stats.reshares.mentionCount"/></dd></div>
                <div v-if="!directMessage" title="Replies and reshares in this conversation, excluding the head post">
                    <dt>Graph engagement</dt><dd><deq-number :number="stats.engagement.mentionCount"/></dd>
                </div>
                <div v-if="!directMessage"><dt>Net Sentiment</dt><dd>
                    <net-sentiment v-if="stats.sentiment.sentimentVerifiedCount" :net-sentiment="netSentiment"/>
                    <span v-else class="deq-callout--muted">No verification</span>
                </dd></div>
            </dl>
            <p class="conversation-info__dates" v-if="stats.posts.firstPublished">
                Conversation active from <deq-date :date="stats.posts.firstPublished"/>
                to <deq-date :date="stats.posts.lastPublished"/><span v-if="!directMessage"> (excluding reshares)</span>.
                <span v-if="!directMessage && stats.reshares.lastPublished">Last reshare: <deq-date :date="stats.reshares.lastPublished"/>.</span>
            </p>
            <details v-if="!directMessage" class="conversation-info__explanation">
                <summary>About these statistics</summary>
                <p>Statistics include public, relevant mentions. Irrelevant mentions may appear in the thread to provide context.</p>
            </details>
        </template>
    </section>
</template>

<script>
import {count, getMentions} from "@/data/Grouse";
import {getPublicSentimentScore} from "@/app/utils/Sentiment";
import DeqDate from "@/components/formatters/DeqDate";
import DeqNumber from "@/components/formatters/DeqNumber";
import NetSentiment from "@/components/NetSentiment";
import InlineLoadingMessage from "@/components/InlineLoadingMessage";

export default {
    name: "ConversationInfo",
    components: {DeqDate, DeqNumber, NetSentiment, InlineLoadingMessage},
    props: {
        filter: {type: String, required: true},
        conversationId: {type: String, default: null},
        accountCode: {type: String, required: true},
        directMessage: {type: Boolean, default: false},
        dateRange: {type: Object, default: null}
    },
    data() {
        return {loading: false, error: false, stats: null};
    },
    computed: {
        netSentiment() {
            return this.stats.sentiment.totalVerifiedSentiment / this.stats.sentiment.sentimentVerifiedCount;
        }
    },
    watch: {filter: 'load'},
    created() { this.requests = []; this.generation = 0; },
    mounted() { this.load(); },
    beforeDestroy() { this.cancel(); },
    methods: {
        cancel() {
            this.generation++;
            this.requests.forEach(request => request.cancel?.());
            this.requests = [];
        },
        async load() {
            this.cancel();
            const generation = this.generation;
            this.loading = true;
            this.error = false;
            this.stats = null;
            const query = (filter, select) => {
                const request = count(filter, null, select, null, null, this.accountCode);
                this.requests.push(request);
                return request;
            };
            const date = (filter, order) => {
                const request = getMentions(filter, [order], 1, false, false, {accountCode: this.accountCode, select: 'published'});
                this.requests.push(request);
                return request;
            };
            try {
                if (this.directMessage) {
                    const [all, first, last] = await Promise.all([
                        query(this.filter, ['mentionCount', 'authorIdCount']),
                        date(this.filter, 'published asc'),
                        date(this.filter, 'published desc')
                    ]);
                    if (generation === this.generation) this.stats = {
                        all, posts: {firstPublished: first[0]?.published, lastPublished: last[0]?.published}
                    };
                    return;
                }
                const [all, reshares, engagement, sentiment, first, last, lastReshare] = await Promise.all([
                    query(this.filter, ['mentionCount', 'authorIdCount']),
                    query(`${this.filter} and reshareof isnt unknown`, ['mentionCount']),
                    query(`${this.filter} and id isnt '${this.conversationId}'`, ['mentionCount']),
                    query(`(${this.filter}) and (${getPublicSentimentScore()})`, ['totalVerifiedSentiment', 'sentimentVerifiedCount']),
                    date(`${this.filter} and reshareof is unknown`, 'published asc'),
                    date(`${this.filter} and reshareof is unknown`, 'published desc'),
                    date(`${this.filter} and reshareof isnt unknown`, 'published desc')
                ]);
                const posts = {firstPublished: first[0]?.published, lastPublished: last[0]?.published};
                if (generation === this.generation) this.stats = {
                    all, posts, reshares: {...reshares, lastPublished: lastReshare[0]?.published}, engagement, sentiment
                };
            } catch (e) {
                if (generation === this.generation) this.error = true;
            } finally {
                if (generation === this.generation) this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.conversation-info { padding: 8px 0 16px; margin: 0 24px; min-height: 110px; box-sizing: border-box; border-top: 1px solid #666; border-bottom: 1px solid #666; }
dl { display: flex; flex-wrap: wrap; gap: 12px 40px; margin: 0 0 12px; }
dl > div { display: flex; flex-direction: column; gap: 4px; }
dt { font-weight: normal; color: var(--be-colour-text-dark); font-size: 12px; }
dd { margin: 0; font-size: 20px; line-height: 24px; }
p { margin: 4px 0; }
.conversation-info__dates, .conversation-info__explanation { font-size: 12px; }
.conversation-info__explanation { color: var(--be-colour-text-dark); }
summary { cursor: pointer; width: fit-content; }
summary:hover { color: var(--be-colour-text-dark__hover); }
</style>
